// API configuration and axios instance
import axios from 'axios';
import { urlConToken } from '../utils/urlConToken';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API_BASE_URL = `${BACKEND_URL}/api`;

// Create axios instance
const api = axios.create({
  baseURL: API_BASE_URL,
});

// Token de la sesión: el de empleado primero (login directo de empleado) y,
// si no, el de administrador. Lo usan el interceptor y los `fetch` crudos (U-68).
export const getAuthToken = () => {
  // Check employee token first (for direct employee login)
  const empleadoToken = localStorage.getItem('empleado_token');
  if (empleadoToken) {
    return empleadoToken;
  }
  // Fall back to admin token
  return localStorage.getItem('token');
};

// Add auth token to requests and handle Content-Type
api.interceptors.request.use((config) => {
  const token = getAuthToken();
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  
  // Only set Content-Type to JSON if data is not FormData
  if (!(config.data instanceof FormData)) {
    config.headers['Content-Type'] = 'application/json';
  }
  // For FormData, let axios set the Content-Type automatically (with boundary)
  
  return config;
});

// Handle auth errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    // Solo redirigir al login si es un 401 en una ruta de autenticación crítica
    // No redirigir por errores 403 (permisos) o 401 en otras rutas
    if (error.response?.status === 401) {
      const url = error.config?.url || '';
      // Solo redirigir si es una ruta que requiere autenticación básica
      // No redirigir por errores en rutas específicas como /empleados, /solicitudes-vacaciones
      // El propio login (contraseña incorrecta) también da 401: ahí no se recarga,
      // para que la página enseñe el error y no pierda el destino al que volver
      const esLogin = url.includes('/auth/login');
      const isAuthRoute = !esLogin && (url.includes('/auth/') || url.includes('/me'));

      if (isAuthRoute) {
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        localStorage.removeItem('empleado_token');
        localStorage.removeItem('empleado_info');
        // Para volver al destino tras entrar (enlaces del móvil y push): Login.js
        // lo lee y lo borra de sessionStorage ('login_desde')
        try {
          const ruta = window.location.pathname || '';
          if (!ruta.startsWith('/login') && !ruta.startsWith('/empleado/login')) {
            sessionStorage.setItem('login_desde', ruta + window.location.search + window.location.hash);
          }
        } catch (e) {
          // sin sessionStorage se entra como siempre, sin volver al destino
        }
        window.location.href = '/login';
      }
    }
    return Promise.reject(error);
  }
);

// Uso del ERP (utils/usoErp.js): observa las respuestas con error del API
// para registrarlas como señal de problema. Va después del interceptor de
// arriba y siempre devuelve el mismo rechazo: un fallo del observador nunca
// cambia el resultado de la llamada.
let observadorErroresApi = null;
export const setObservadorErroresApi = (fn) => {
  observadorErroresApi = typeof fn === 'function' ? fn : null;
};
api.interceptors.response.use(
  (response) => response,
  (error) => {
    try {
      if (observadorErroresApi) observadorErroresApi(error);
    } catch (e) {
      // medir el uso nunca rompe una llamada
    }
    return Promise.reject(error);
  }
);

export default api;
export { api }; // También exportar como named export para poder importarlo así: import { api } from './api'

// Auth API
export const authAPI = {
  login: (email, password) => api.post('/auth/login', { email, password }),
  register: (data) => api.post('/auth/register', data),
  me: () => api.get('/auth/me'),
};

// Clients API
export const clientesAPI = {
  getAll: (estado = null, { incluir_archivados = false, page, page_size } = {}) =>
    api.get('/clientes', { params: { estado, incluir_archivados, page, page_size } }),
  getOne: (id) => api.get(`/clientes/${id}`),
  create: (data) => api.post('/clientes', data),
  update: (id, data) => api.put(`/clientes/${id}`, data),
  delete: (id, { force = false } = {}) => api.delete(`/clientes/${id}`, { params: { force } }),
  restaurar: (id) => api.post(`/clientes/${id}/restaurar`),
  getEstadisticas: (id) => api.get(`/clientes/${id}/estadisticas`),
  getEstadisticasPago: (id) => api.get(`/clientes/${id}/estadisticas-pago`),
  getActividadResumen: () => api.get('/clientes/actividad-resumen'),
  getTags: () => api.get('/clientes/tags'),
  getFacturasPendientes: (id) => api.get(`/clientes/${id}/facturas-pendientes`),
};

// Interacciones CRM (timeline de actividad por cliente)
export const interaccionesAPI = {
  list: (clienteId) => api.get(`/clientes/${clienteId}/interacciones`),
  create: (clienteId, data) => api.post(`/clientes/${clienteId}/interacciones`, data),
  update: (id, data) => api.put(`/interacciones/${id}`, data),
  delete: (id) => api.delete(`/interacciones/${id}`),
};

// Sectores de empresa
export const sectoresAPI = {
  getAll: () => api.get('/sectores'),
  create: (data) => api.post('/sectores', data),
  update: (id, data) => api.put(`/sectores/${id}`, data),
  delete: (id) => api.delete(`/sectores/${id}`),
};

// Employees API
export const empleadosAPI = {
  getAll: (activo = null) => api.get('/empleados', { params: { activo } }),
  getOne: (id) => api.get(`/empleados/${id}`),
  create: (data) => api.post('/empleados', data),
  update: (id, data) => api.put(`/empleados/${id}`, data),
  delete: (id) => api.delete(`/empleados/${id}`),
  // Solo PATCH (dirección y RRHH); el PUT /permisos ya no existe
  updatePermisos: (id, permisos_modulos) => api.patch(`/empleados/${id}/permisos`, { permisos_modulos }),
  generarPasswordPortal: (id) => api.post(`/empleados/${id}/generar-password-portal`),
  setPassword: (id, password) => api.post(`/empleados/${id}/set-password`, { password }),
  invitacionPortal: (id, enviarEmail) => api.post(`/empleados/${id}/invitacion-portal`, { enviar_email: enviarEmail }),
  // Tour de bienvenida (dirección y RRHH): true lo activa (o lo repite), false lo quita
  tourBienvenida: (id, pendiente) => api.post(`/empleados/${id}/tour-bienvenida`, { pendiente }),
  // Métodos para documentos, cursos (si se implementan)
  getDocumentos: (id) => api.get(`/empleados/${id}/documentos`),
  addDocumento: (id, data) => api.post(`/empleados/${id}/documentos`, data),
  uploadDocumento: (id, formData) => api.post(`/empleados/${id}/documentos/upload`, formData),
  deleteDocumento: (empleadoId, documentoId) => api.delete(`/empleados/${empleadoId}/documentos/${documentoId}`),
  getCursos: (id) => api.get(`/empleados/${id}/cursos`),
  addCurso: (id, data) => api.post(`/empleados/${id}/cursos`, data),
  deleteCurso: (empleadoId, cursoId) => api.delete(`/empleados/${empleadoId}/cursos/${cursoId}`),
  uploadCertificadoCurso: (empleadoId, cursoId, formData) =>
    api.post(`/empleados/${empleadoId}/cursos/${cursoId}/upload-certificado`, formData),
};

// Texto legible del `detail` de un error del API: los 422 de pydantic llegan
// como lista ({loc, msg}) y se muestran "campo: mensaje", uno por línea.
export const textoErrorApi = (detail, porDefecto = 'Error') => {
  if (!detail) return porDefecto;
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) {
    const lineas = detail.map((d) => {
      if (!d || typeof d !== 'object') return String(d);
      const campo = (d.loc || []).filter((p) => p !== 'body' && p !== 'query').join('.');
      const msg = d.msg || 'valor no válido';
      return campo ? `${campo}: ${msg}` : msg;
    });
    return lineas.length ? lineas.join('\n') : porDefecto;
  }
  if (detail.mensaje) return detail.mensaje;
  try { return JSON.stringify(detail); } catch (e) { return porDefecto; }
};

// Providers API
export const proveedoresAPI = {
  getAll: () => api.get('/proveedores'),
  getOne: (id) => api.get(`/proveedores/${id}`),
  create: (data) => api.post('/proveedores', data),
  update: (id, data) => api.put(`/proveedores/${id}`, data),
  delete: (id) => api.delete(`/proveedores/${id}`),
  getIngredientesRelacionados: (id) => api.get(`/proveedores/${id}/ingredientes-relacionados`),
};

// Menus API
export const menusAPI = {
  getAll: () => api.get('/menus'),
  getOne: (id) => api.get(`/menus/${id}`),
  create: (data) => api.post('/menus', data),
  update: (id, data) => api.put(`/menus/${id}`, data),
  delete: (id) => api.delete(`/menus/${id}`),
  toggleRevisado: (id) => api.patch(`/menus/${id}/toggle-revisado`),
  toggleActivo: (id) => api.patch(`/menus/${id}/toggle-activo`),
};

// Dishes API
export const platosAPI = {
  getAll: (activo = null) => api.get('/platos', { params: { activo } }),
  getOne: (id) => api.get(`/platos/${id}`),
  create: (data) => api.post('/platos', data),
  update: (id, data) => api.put(`/platos/${id}`, data),
  delete: (id) => api.delete(`/platos/${id}`),
  getRecomendaciones: (id, alergenos_evitar = '', tipo_adaptacion = null, rango_precio_porcentaje = 20, limit = 10, caracteristicas_evitar = '') => 
    api.get(`/platos/${id}/recomendaciones`, { 
      params: { alergenos_evitar, tipo_adaptacion, caracteristicas_evitar, rango_precio_porcentaje, limit } 
    }),
  toggleRevisado: (id) => api.patch(`/platos/${id}/toggle-revisado`),
  toggleActivo: (id) => api.patch(`/platos/${id}/toggle-activo`),
  getMenusAfectados: (id) => api.get(`/platos/${id}/menus-afectados`),
  desactivar: (id, data) => api.post(`/platos/${id}/desactivar`, data),
  getBebidas: () => api.get('/catalogo/platos-bebidas'),
};

// Ingredients API
export const ingredientesAPI = {
  getAll: (activo = null, proveedor_id = null) => api.get('/ingredientes', { params: { activo, proveedor_id } }),
  getOne: (id) => api.get(`/ingredientes/${id}`),
  create: (data) => api.post('/ingredientes', data),
  update: (id, data) => api.put(`/ingredientes/${id}`, data),
  delete: (id) => api.delete(`/ingredientes/${id}`),
  toggleRevisado: (id) => api.patch(`/ingredientes/${id}/toggle-revisado`),
  toggleActivo: (id) => api.patch(`/ingredientes/${id}/toggle-activo`),
  getPlatos: (id, anio = null) => api.get(`/ingredientes/${id}/platos`, { params: anio ? { anio } : {} }),
};

// Allergens API
export const alergenosAPI = {
  getAll: () => api.get('/alergenos'),
  getOne: (id) => api.get(`/alergenos/${id}`),
  create: (data) => api.post('/alergenos', data),
  update: (id, data) => api.put(`/alergenos/${id}`, data),
  delete: (id) => api.delete(`/alergenos/${id}`),
};

// Budgets API
export const presupuestosAPI = {
  getAll: (params = {}) => 
    api.get('/presupuestos', { params }),
  getOne: (id) => api.get(`/presupuestos/${id}`),
  // Eventos del calendario (jornadas de multievento sueltas) en un rango de fechas
  getCalendario: (params = {}) => api.get('/presupuestos/calendario', { params }),
  create: (data) => api.post('/presupuestos', data),
  update: (id, data) => api.put(`/presupuestos/${id}`, data),
  delete: (id) => api.delete(`/presupuestos/${id}`),
  confirmar: (id) => api.post(`/presupuestos/${id}/confirmar`),
  desconfirmar: (id) => api.post(`/presupuestos/${id}/desconfirmar`),
  // Cancelado o rechazado -> borrador (no rehace nada: se vuelve a confirmar a mano)
  reabrir: (id) => api.post(`/presupuestos/${id}/reabrir`),
  // Cancelar: lo que hará (solo lectura) y la cancelación con motivo y pedidos a anular
  // body: {motivo, comentario, importe_facturado_cancelacion, anular_pedidos: [{tipo, id, jornada_id, enviar_email}]}
  cancelacionPendientes: (id) => api.get(`/presupuestos/${id}/cancelacion/pendientes`),
  cancelar: (id, body) => api.post(`/presupuestos/${id}/cancelar`, body),
  // Quick edit functions
  addPlatoToMenu: (presupuestoId, menuIndex, platoData) => 
    api.post(`/presupuestos/${presupuestoId}/menus/${menuIndex}/platos`, platoData),
  removePlatoFromMenu: (presupuestoId, menuIndex, platoId) => 
    api.delete(`/presupuestos/${presupuestoId}/menus/${menuIndex}/platos/${platoId}`),
  // Bebidas
  getBebidas: (presupuestoId) => api.get(`/presupuestos/${presupuestoId}/bebidas`),
  addBebida: (presupuestoId, bebidaData) => api.post(`/presupuestos/${presupuestoId}/bebidas`, bebidaData),
  updateBebida: (presupuestoId, bebidaId, bebidaData) => api.put(`/presupuestos/${presupuestoId}/bebidas/${bebidaId}`, bebidaData),
  deleteBebida: (presupuestoId, bebidaId) => api.delete(`/presupuestos/${presupuestoId}/bebidas/${bebidaId}`),
  confirmarBebidas: (presupuestoId) => api.post(`/presupuestos/${presupuestoId}/bebidas/confirmar`),
  // Modo de bebidas: variado sin alcohol, variado con alcohol o personalizado.
  // Elegir un modo lo deja validado.
  getBebidasModo: (presupuestoId) => api.get(`/presupuestos/${presupuestoId}/bebidas-modo`),
  setBebidasModo: (presupuestoId, modo) => api.put(`/presupuestos/${presupuestoId}/bebidas-modo`, { modo }),
  // Ajuste de unidades del reparto: { unidades: {ingrediente_id: n}, extra: 'cobrar' | 'cortesia' | null }
  setBebidasUnidades: (presupuestoId, unidades, extra = null, jornadaId = null) =>
    api.put(`/presupuestos/${presupuestoId}/bebidas-unidades`, { unidades, extra, jornada_id: jornadaId }),
  // Barra libre del lunch: chip en la línea del lunch (crea o borra la línea
  // "Barra libre lunch" vinculada). Body: {linea_id, jornada_id, indice, activar}
  getBarraLibreLunch: (presupuestoId) => api.get(`/presupuestos/${presupuestoId}/barra-libre-lunch`),
  setBarraLibreLunch: (presupuestoId, body) => api.post(`/presupuestos/${presupuestoId}/barra-libre-lunch`, body),
  // Último envío al cliente y cambios desde entonces ("Modificado tras el envío")
  getEstadoEnvio: (id) => api.get(`/presupuestos/${id}/estado-envio`),
  // Cobro previo (100 % siete días antes del evento) y petición de pago al cliente
  getCobroPrevio: (id) => api.get(`/presupuestos/${id}/cobro-previo`),
  pedirCobroPrevio: (id, body) => api.post(`/presupuestos/${id}/cobro-previo/pedir`, body),
  descargarPdf: (id) => api.get(`/presupuestos/${id}/generar-pdf`, { responseType: 'blob' }),
  // Orden de personal extra (solicitud al proveedor de camareros)
  ordenPersonalPdfUrl: (id, jornadaId = null) => {
    // El PDF se abre con window.open, así que el token viaja por query
    // (el endpoint usa `get_current_user_optional`, igual que el resto de PDFs).
    const base = `${process.env.REACT_APP_BACKEND_URL || ''}/api/presupuestos/${id}/orden-personal/pdf`;
    const params = new URLSearchParams();
    params.append('token', getAuthToken() || '');
    if (jornadaId) params.append('jornada_id', jornadaId);
    return `${base}?${params.toString()}`;
  },
  enviarOrdenPersonal: (id, jornadaId = null) =>
    api.post(`/presupuestos/${id}/orden-personal/enviar`, { jornada_id: jornadaId }),
  // Una jornada del multievento: solo los campos enviados (`borrar`: campos a vaciar)
  updateJornada: (id, jornadaId, body) => api.put(`/presupuestos/${id}/jornadas/${jornadaId}`, body),
  // Gestión de jornadas del multievento (pestaña Líneas y menús del formato nuevo)
  actualizarJornada: (id, jornadaId, body) => api.put(`/presupuestos/${id}/jornadas/${jornadaId}`, body),
  crearJornada: (id, body) => api.post(`/presupuestos/${id}/jornadas`, body),
  // Multievento: hora, tipo y "Acompaña a" de un servicio de la cabecera y de
  // ese servicio en cada jornada. linea: su linea_id o su índice en la cabecera.
  // body: {hora_inicio?, hora_fin?, requiere_horario?, acompana_a_linea_id?,
  // acompana_a_indice?, menu_id?, alcance: 'sincronizadas' | 'todas'}
  horarioServicio: (id, linea, body) =>
    api.put(`/presupuestos/${id}/servicios/${encodeURIComponent(linea)}/horario`, body),
  // Servicios sin hora, sin fin, imposibles o fuera del horario del día antes
  // de confirmar o de la orden de cocina nueva (solo lectura, abierto a todos)
  // y la clasificación de las líneas: {avisos: [{tipo, texto, ...}], horarios}
  avisosHorarios: (id, jornadaId = null) =>
    api.get(`/presupuestos/${id}/horarios/avisos`, { params: jornadaId ? { jornada_id: jornadaId } : {} }),
  // body: {fecha_inicio, fecha_fin, excluir_fines_semana, hora_inicio, hora_fin, num_personas}
  generarJornadas: (id, body) => api.post(`/presupuestos/${id}/jornadas/generar`, body),
  duplicarJornada: (id, jornadaId, nuevaFecha) =>
    api.post(`/presupuestos/${id}/jornadas/${jornadaId}/duplicar`, null, { params: { nueva_fecha: nuevaFecha } }),
  eliminarJornada: (id, jornadaId) => api.delete(`/presupuestos/${id}/jornadas/${jornadaId}`),
  // Evento de un día -> multievento (primera jornada con los datos actuales)
  convertirAMultiEvento: (id) => api.post(`/presupuestos/${id}/convertir-a-multi-evento`),
  // Personas de una jornada en un paso: jornada, líneas con el mismo número,
  // totales, suplemento mínimo de la jornada, tareas y cobro
  cambiarPersonasJornada: (id, jornadaId, numPersonas, aplicarALineas = true) =>
    api.put(`/presupuestos/${id}/jornadas/${jornadaId}/personas`, { num_personas: numPersonas, aplicar_a_lineas: aplicarALineas }),
  // Multievento: añadir o quitar una línea en las jornadas elegidas (todas si
  // jornadas_ids es null) y llevar a las jornadas lo que solo está en la cabecera
  anadirLineaMultievento: (id, body) => api.post(`/presupuestos/${id}/multievento/lineas`, body),
  quitarLineaMultievento: (id, body) => api.post(`/presupuestos/${id}/multievento/lineas/quitar`, body),
  llevarCabeceraMultievento: (id, indices = null) =>
    api.post(`/presupuestos/${id}/multievento/llevar-cabecera`, indices ? { indices } : {}),
  // Multievento, fuente única: líneas de jornada con precio o IVA distinto a la
  // cabecera (solo lectura) e "Igualar jornadas a la cabecera"
  divergenciasCabeceraMultievento: (id) => api.get(`/presupuestos/${id}/multievento/divergencias-cabecera`),
  igualarCabeceraMultievento: (id) => api.post(`/presupuestos/${id}/multievento/igualar-cabecera`),
  // Duplicar el presupuesto entero con fecha nueva (y cliente o nombre opcionales)
  duplicar: (id, body) => api.post(`/presupuestos/${id}/duplicar`, body),
  // Alquiler de material del evento (líneas, validación y orden al proveedor)
  getAlquiler: (id, jornadaId = null) =>
    api.get(`/presupuestos/${id}/alquiler`, { params: jornadaId ? { jornada_id: jornadaId } : {} }),
  setAlquiler: (id, body, jornadaId = null) =>
    api.put(`/presupuestos/${id}/alquiler`, body, { params: jornadaId ? { jornada_id: jornadaId } : {} }),
  validarAlquiler: (id, jornadaId = null) =>
    api.post(`/presupuestos/${id}/alquiler/validar`, {}, { params: jornadaId ? { jornada_id: jornadaId } : {} }),
  reabrirAlquiler: (id, jornadaId = null) =>
    api.post(`/presupuestos/${id}/alquiler/reabrir`, {}, { params: jornadaId ? { jornada_id: jornadaId } : {} }),
  // Recepción en el evento: { comprobada, incidencias }
  recepcionAlquiler: (id, body, jornadaId = null) =>
    api.post(`/presupuestos/${id}/alquiler/recepcion`, body, { params: jornadaId ? { jornada_id: jornadaId } : {} }),
  setOrdenAlquiler: (id, body, jornadaId = null) =>
    api.put(`/presupuestos/${id}/orden-alquiler`, body, { params: jornadaId ? { jornada_id: jornadaId } : {} }),
  descargarOrdenAlquilerPdf: (id, jornadaId = null) =>
    api.get(`/presupuestos/${id}/orden-alquiler/pdf`, {
      params: { formato: 'pdf', ...(jornadaId ? { jornada_id: jornadaId } : {}) },
      responseType: 'blob',
    }),
  enviarOrdenAlquiler: (id, body = {}, jornadaId = null) =>
    api.post(`/presupuestos/${id}/orden-alquiler/enviar`, body, { params: jornadaId ? { jornada_id: jornadaId } : {} }),
  confirmarOrdenAlquiler: (id, body, jornadaId = null) =>
    api.post(`/presupuestos/${id}/orden-alquiler/confirmar`, body, { params: jornadaId ? { jornada_id: jornadaId } : {} }),
  estadoOrdenAlquiler: (id, body, jornadaId = null) =>
    api.post(`/presupuestos/${id}/orden-alquiler/estado`, body, { params: jornadaId ? { jornada_id: jornadaId } : {} }),
  // Cotización recibida del proveedor por correo (PDF leído y comparado con la orden)
  aplicarCotizacionAlquiler: (id, jornadaId = null) =>
    api.post(`/presupuestos/${id}/orden-alquiler/cotizacion/aplicar`, {}, { params: jornadaId ? { jornada_id: jornadaId } : {} }),
  descartarCotizacionAlquiler: (id, jornadaId = null) =>
    api.post(`/presupuestos/${id}/orden-alquiler/cotizacion/descartar`, {}, { params: jornadaId ? { jornada_id: jornadaId } : {} }),
};

// Pestaña "Lista de compra" de la ficha (motor nuevo, en paralelo a Compras).
// jornadaId null = todo el evento.
const paramsListaCompra = (jornadaId, extra = {}) => ({ ...(jornadaId ? { jornada_id: jornadaId } : {}), ...extra });
export const listaCompraV2API = {
  get: (id, jornadaId = null) =>
    api.get(`/presupuestos/${id}/lista-compra-v2`, { params: paramsListaCompra(jornadaId) }),
  // body: {estado, comprador, nota} o solo {no_pedir} / {paquetes_manual} / {cantidad_manual} /
  // {dias: [{fecha, paquetes}]} (null = volver al calculado), siempre con jornada_id
  guardarLinea: (id, ingredienteId, body) =>
    api.put(`/presupuestos/${id}/lista-compra-v2/lineas/${ingredienteId}`, body),
  descargarPdf: (id, jornadaId = null, proveedorId = null, fechaEntrega = null) =>
    api.get(`/presupuestos/${id}/lista-compra-v2/pdf`, {
      params: paramsListaCompra(jornadaId, paramsPedido(proveedorId, fechaEntrega)),
      responseType: 'blob',
    }),
  whatsapp: (id, jornadaId = null, proveedorId = null, fechaEntrega = null) =>
    api.get(`/presupuestos/${id}/lista-compra-v2/whatsapp`, {
      params: paramsListaCompra(jornadaId, paramsPedido(proveedorId, fechaEntrega)),
    }),
  // Tras el PDF o el WhatsApp del pedido: líneas del proveedor a "pedido" sin orden. body {jornada_id?, proveedor_id, fecha_entrega?}
  marcarPedidas: (id, body) =>
    api.post(`/presupuestos/${id}/lista-compra-v2/marcar-pedidas`, body),
  comparativa: (id, jornadaId = null) =>
    api.get(`/presupuestos/${id}/lista-compra-v2/comparativa`, { params: paramsListaCompra(jornadaId) }),
  // Orden a proveedor (sin enviar) con lo que falta: body {jornada_id?, fecha_entrega?, solo_falta, lineas?}
  crearOrden: (id, proveedorId, body) =>
    api.post(`/presupuestos/${id}/lista-compra-v2/crear-orden/${proveedorId}`, body),
  // Compra de la semana (Compras y Almacén): {desde, hasta, presupuestoIds?, soloFalta?}
  combinada: ({ desde, hasta, presupuestoIds = null, soloFalta = false } = {}) =>
    api.get('/lista-compra-v2/combinada', { params: paramsCombinada(desde, hasta, presupuestoIds, { solo_falta: soloFalta ? 1 : 0 }) }),
  // soloFalta: pedido complementario (solo lo que falta sobre lo ya pedido)
  combinadaPdf: ({ desde, hasta, presupuestoIds = null, proveedorId = null, fechaEntrega = null, soloFalta = false } = {}) =>
    api.get('/lista-compra-v2/combinada/pdf', {
      params: paramsCombinada(desde, hasta, presupuestoIds, { ...paramsPedido(proveedorId, fechaEntrega), ...(soloFalta ? { solo_falta: 1 } : {}) }),
      responseType: 'blob',
    }),
  combinadaWhatsapp: ({ desde, hasta, presupuestoIds = null, proveedorId = null, fechaEntrega = null, soloFalta = false } = {}) =>
    api.get('/lista-compra-v2/combinada/whatsapp', {
      params: paramsCombinada(desde, hasta, presupuestoIds, { ...paramsPedido(proveedorId, fechaEntrega), ...(soloFalta ? { solo_falta: 1 } : {}) }),
    }),
  // "No pedir" (se propaga a cada evento) o cantidades a mano (solo para este rango):
  // body {presupuesto_ids, desde, hasta, no_pedir? | paquetes_manual? | cantidad_manual? | dias?}
  combinadaGuardarLinea: (ingredienteId, body) =>
    api.put(`/lista-compra-v2/combinada/lineas/${ingredienteId}`, body),
  // body {presupuesto_ids, desde, hasta, proveedor_id, fecha_entrega?, solo_falta?}
  // (solo_falta: el pedido complementario se suma a lo ya pedido)
  combinadaMarcarPedidas: (body) =>
    api.post('/lista-compra-v2/combinada/marcar-pedidas', body),
  // Estado de una línea en todos los eventos del rango:
  // body {presupuesto_ids, desde, hasta, estado: 'pendiente' | 'pedido' | 'comprado'}
  combinadaEstadoLinea: (ingredienteId, body) =>
    api.put(`/lista-compra-v2/combinada/lineas/${ingredienteId}/estado`, body),
  // Todo un proveedor como pedido o comprado:
  // body {presupuesto_ids, desde, hasta, proveedor_id, estado: 'pedido' | 'comprado'}
  combinadaEstadoProveedor: (body) =>
    api.post('/lista-compra-v2/combinada/estado-proveedor', body),
  // Pedidos ya hechos que se han quedado cortos (o sobran): {dias?, desde?, hasta?}
  avisosPedidos: ({ dias = null, desde = null, hasta = null } = {}) =>
    api.get('/lista-compra-v2/avisos-pedidos', {
      params: { ...(dias !== null ? { dias } : {}), ...(desde ? { desde } : {}), ...(hasta ? { hasta } : {}) },
    }),
  // body {proveedor_id, huella}
  avisosPedidosRevisado: (body) =>
    api.post('/lista-compra-v2/avisos-pedidos/revisado', body),
  // body {presupuesto_ids, desde, hasta, fecha_entrega?, lineas?}
  crearOrdenCombinada: (proveedorId, body) =>
    api.post(`/lista-compra-v2/combinada/crear-orden/${proveedorId}`, body),
};

// Proveedor y fecha de entrega (o de compra) del PDF y el WhatsApp del pedido
function paramsPedido(proveedorId, fechaEntrega) {
  return {
    ...(proveedorId ? { proveedor_id: proveedorId } : {}),
    ...(fechaEntrega ? { fecha_entrega: fechaEntrega } : {}),
  };
}

// Parámetros de la compra de la semana (los ids van separados por comas)
function paramsCombinada(desde, hasta, presupuestoIds, extra = {}) {
  const params = { ...extra };
  if (desde) params.desde = desde;
  if (hasta) params.hasta = hasta;
  if (Array.isArray(presupuestoIds) && presupuestoIds.length > 0) params.presupuesto_ids = presupuestoIds.join(',');
  return params;
}

// Orden de cocina (nueva) con el motor de la lista de compra, en pruebas: la
// orden que se envía sigue siendo la actual (generar-orden-cocina).
export const ordenCocinaV2API = {
  descargar: (id, jornadaId = null, formato = 'pdf') =>
    api.get(`/presupuestos/${id}/orden-cocina-nueva`, {
      params: paramsListaCompra(jornadaId, { formato }),
      responseType: 'blob',
    }),
  comparativa: (id, jornadaId = null) =>
    api.get(`/presupuestos/${id}/orden-cocina-nueva/comparativa`, { params: paramsListaCompra(jornadaId) }),
  // Varias órdenes en un solo PDF: el servidor las genera en segundo plano
  loteCrear: (datos) => api.post('/orden-cocina-nueva/lote', datos),
  loteEstado: (loteId) => api.get(`/orden-cocina-nueva/lote/${loteId}`),
  lotePdf: (loteId) => api.get(`/orden-cocina-nueva/lote/${loteId}/pdf`, { responseType: 'blob' }),
  loteCancelar: (loteId) => api.delete(`/orden-cocina-nueva/lote/${loteId}`),
};

// Dashboard API
export const dashboardAPI = {
  getStats: () => api.get('/dashboard/stats'),
};

// Validación comercial (pre-facturación) API
export const validacionComercialAPI = {
  getPendientes: () => api.get('/validacion-comercial/pendientes'),
  validar: (id, jornadaId = null) =>
    api.post(`/validacion-comercial/${id}/validar`, {}, jornadaId ? { params: { jornada_id: jornadaId } } : {}),
};

// Personas de Contacto API
export const personasContactoAPI = {
  getAll: (cliente_id = null) => api.get('/personas-contacto', { params: { cliente_id } }),
  getOne: (id) => api.get(`/personas-contacto/${id}`),
  create: (data) => api.post('/personas-contacto', data),
  update: (id, data) => api.put(`/personas-contacto/${id}`, data),
  delete: (id) => api.delete(`/personas-contacto/${id}`),
};

// Ubicaciones API
export const ubicacionesAPI = {
  getAll: () => api.get('/ubicaciones'),
  getOne: (id) => api.get(`/ubicaciones/${id}`),
  create: (data) => api.post('/ubicaciones', data),
  update: (id, data) => api.put(`/ubicaciones/${id}`, data),
  delete: (id) => api.delete(`/ubicaciones/${id}`),
};

// Materiales API
export const materialesAPI = {
  getAll: (categoria = null, activo = null) => api.get('/materiales', { params: { categoria, activo } }),
  getOne: (id) => api.get(`/materiales/${id}`),
  create: (data) => api.post('/materiales', data),
  update: (id, data) => api.put(`/materiales/${id}`, data),
  delete: (id) => api.delete(`/materiales/${id}`),
  toggleRevisado: (id) => api.patch(`/materiales/${id}/toggle-revisado`),
  toggleActivo: (id) => api.patch(`/materiales/${id}/toggle-activo`),
  getDisponibilidad: (id) => api.get(`/materiales/${id}/disponibilidad`),
  getDisponibilidadSemana: (dias = 7) => api.get('/materiales/disponibilidad', { params: { dias } }),
  notificarDeficit: (dias = 7) => api.post('/materiales/notificar-deficit', null, { params: { dias } }),
  // Buscador del catálogo de alquiler (q por palabras sin acentos, sección y proveedor)
  buscarAlquiler: ({ q = '', seccion = '', proveedor_id = '' } = {}) => {
    const params = { tipo_coste: 'alquiler' };
    if (q) params.q = q;
    if (seccion) params.seccion = seccion;
    if (proveedor_id) params.proveedor_id = proveedor_id;
    return api.get('/materiales', { params });
  },
  // Veces que se ha alquilado cada material de alquiler (eventos confirmados)
  usoAlquiler: () => api.get('/materiales/uso-alquiler'),
};

// Catálogos con fotos de los proveedores de alquiler (visor de la ficha)
export const catalogosAlquilerAPI = {
  listar: () => api.get('/catalogos-alquiler'),
  manifest: (clave) => api.get(`/catalogos-alquiler/${encodeURIComponent(clave)}/manifest`),
  // URL de la imagen de una página: el <img> no manda cabeceras, así que el
  // token viaja por query (el endpoint usa get_current_user_optional)
  paginaUrl: (clave, imagen) => {
    const base = `${process.env.REACT_APP_BACKEND_URL || ''}/api/catalogos-alquiler/${encodeURIComponent(clave)}/pagina/${encodeURIComponent(imagen)}`;
    return `${base}?${new URLSearchParams({ token: getAuthToken() || '' }).toString()}`;
  },
};

// Plantillas Menaje API
export const plantillasMenajeAPI = {
  getAll: (tipo_menu = null) => api.get('/plantillas-menaje', { params: { tipo_menu } }),
  getOne: (id) => api.get(`/plantillas-menaje/${id}`),
  create: (data) => api.post('/plantillas-menaje', data),
  update: (id, data) => api.put(`/plantillas-menaje/${id}`, data),
  delete: (id) => api.delete(`/plantillas-menaje/${id}`),
};

// Alias para compatibilidad
export const plantillasAPI = plantillasMenajeAPI;

// Stock API (placeholder)
export const stockAPI = {
  getAll: () => api.get('/stock'),
  getOne: (id) => api.get(`/stock/${id}`),
  create: (data) => api.post('/stock', data),
  update: (id, data) => api.put(`/stock/${id}`, data),
  delete: (id) => api.delete(`/stock/${id}`),
};

// Materiales Asignados API. U-89 (lote 5b): filtros de verdad (estado y fecha
// del evento) y cambio de estado, que la pantalla llamaba y no existía
export const materialesAsignadosAPI = {
  getAll: (params = {}) => api.get('/materiales-asignados', { params }),
  cambiarEstado: (id, estado) => api.patch(`/materiales-asignados/${id}/estado`, null, { params: { estado } }),
  getOne: (id) => api.get(`/materiales-asignados/${id}`),
  create: (data) => api.post('/materiales-asignados', data),
  update: (id, data) => api.put(`/materiales-asignados/${id}`, data),
  delete: (id) => api.delete(`/materiales-asignados/${id}`),
};

// Libro único de stock (lote 5b): diagnóstico en seco, consumo de reservas
// celebradas, reconstrucción de db.stock, copias previas y flags (dirección)
export const stockLibroAPI = {
  diagnostico: () => api.get('/stock/diagnostico-libro'),
  diagnosticoCsv: () => api.get('/stock/diagnostico-libro', { params: { formato: 'csv' }, responseType: 'blob' }),
  consumirReservas: (body = { dry_run: true }) => api.post('/stock/consumir-reservas-celebradas', body),
  reconstruir: (body = { dry_run: true }) => api.post('/stock/reconstruir-desde-lotes', body),
  inventarioACero: (body = { dry_run: true }) => api.post('/stock/inventario-a-cero', body),
  respaldos: () => api.get('/stock/respaldos'),
  restaurar: (nombre) => api.post(`/stock/respaldos/${encodeURIComponent(nombre)}/restaurar`, { confirmado: true }),
  configuracion: () => api.get('/stock/configuracion-libro'),
  guardarConfiguracion: (cambios) => api.post('/stock/configuracion-libro', { ...cambios, confirmado: true }),
};

// Tareas API (placeholder)
export const tareasAPI = {
  getAll: () => api.get('/tareas'),
  getOne: (id) => api.get(`/tareas/${id}`),
  create: (data) => api.post('/tareas', data),
  update: (id, data) => api.put(`/tareas/${id}`, data),
  delete: (id) => api.delete(`/tareas/${id}`),
};

// Control horario: fichajes de un empleado y fichaje manual (dirección y RRHH).
// Las horas viajan como ISO 8601 con zona.
export const controlHorarioAPI = {
  getRegistrosByEmpleado: (empleadoId, mes = null, anio = null) => {
    const params = new URLSearchParams();
    if (mes) params.append('mes', mes);
    if (anio) params.append('anio', anio);
    const queryString = params.toString();
    return api.get(`/fichaje/empleado/${empleadoId}${queryString ? `?${queryString}` : ''}`);
  },
  crearFichajeManual: (data) => {
    const params = new URLSearchParams();
    params.append('empleado_id', data.empleado_id);
    params.append('fecha', data.fecha);
    if (data.hora_entrada) params.append('hora_entrada', data.hora_entrada);
    if (data.hora_salida) params.append('hora_salida', data.hora_salida);
    if (data.notas) params.append('notas', data.notas);
    return api.post(`/fichaje/manual?${params.toString()}`);
  },
  editarFichajeManual: (registroId, data) => {
    const params = new URLSearchParams();
    if (data.hora_entrada) params.append('hora_entrada', data.hora_entrada);
    if (data.hora_salida) params.append('hora_salida', data.hora_salida);
    if (data.notas !== undefined) params.append('notas', data.notas);
    return api.put(`/fichaje/manual/${registroId}?${params.toString()}`);
  },
  eliminarFichajeManual: (registroId) => api.delete(`/fichaje/manual/${registroId}`)
};

// Vehículos API
export const vehiculosAPI = {
  getAll: (params = {}) => {
    const queryParams = new URLSearchParams();
    if (params.estado) queryParams.append('estado', params.estado);
    if (params.tipo) queryParams.append('tipo', params.tipo);
    const queryString = queryParams.toString();
    return api.get(`/vehiculos${queryString ? `?${queryString}` : ''}`);
  },
  getOne: (id) => api.get(`/vehiculos/${id}`),
  create: (data) => api.post('/vehiculos', data),
  update: (id, data) => api.put(`/vehiculos/${id}`, data),
  delete: (id) => api.delete(`/vehiculos/${id}`),
  // Kilometraje
  registrarKilometraje: (vehiculoId, data) => api.post(`/vehiculos/${vehiculoId}/kilometraje`, data),
  // Mantenimiento
  getMantenimientos: (vehiculoId) => api.get(`/vehiculos/${vehiculoId}/mantenimiento`),
  registrarMantenimiento: (vehiculoId, data) => api.post(`/vehiculos/${vehiculoId}/mantenimiento`, data),
  // Multas
  registrarMulta: (vehiculoId, data) => api.post(`/vehiculos/${vehiculoId}/multas`, data),
  pagarMulta: (multaId) => api.put(`/vehiculos/multas/${multaId}/pagar`),
  // Estadísticas
  getResumen: () => api.get('/vehiculos/stats/resumen'),
  // Asignación a eventos
  getDisponibilidad: (fecha) => api.get(`/vehiculos/disponibilidad/${fecha}`),
  asignarEvento: (presupuestoId, data) => api.post(`/vehiculos/asignar-evento?presupuesto_id=${presupuestoId}`, data),
  desasignarEvento: (presupuestoId, asignacionId) => api.delete(`/vehiculos/desasignar-evento/${presupuestoId}/${asignacionId}`),
  getAsignadosEvento: (presupuestoId) => api.get(`/vehiculos/asignados-evento/${presupuestoId}`)
};

// Precio del combustible (API del Ministerio, gasolinera habitual)
export const combustibleAPI = {
  getPrecio: (fecha) => api.get(`/combustible/precio${fecha ? `?fecha=${fecha}` : ''}`),
  actualizar: () => api.post('/combustible/actualizar'),
};

// Emails / Buzón integrado (IMAP + SMTP contra cPanel u otro servidor)
export const emailsAPI = {
  listarCuentas: () => api.get('/emails/cuentas'),
  crearCuenta: (data) => api.post('/emails/cuentas', data),
  actualizarCuenta: (id, data) => api.patch(`/emails/cuentas/${id}`, data),
  eliminarCuenta: (id) => api.delete(`/emails/cuentas/${id}`),
  probarCuenta: (id) => api.post(`/emails/cuentas/${id}/probar`),
  usuariosDisponibles: () => api.get('/emails/cuentas/usuarios-disponibles'),
  listar: (params = {}) => api.get('/emails', { params }),
  obtener: (id) => api.get(`/emails/${id}`),
  actualizar: (id, cambios) => api.patch(`/emails/${id}`, cambios),
  enviar: (data) => api.post('/emails/enviar', data),
  // Borradores reales del servidor (solo los del buzón propio)
  abrirBorrador: (id) => api.get(`/emails/${id}/borrador`),
  guardarBorrador: (data) => api.post('/emails/borradores', data),
  eliminarBorrador: (id) => api.delete(`/emails/${id}/borrador`),
  sync: () => api.post('/emails/sync'),
  resumenDia: (params = {}) => api.get('/emails/resumen-dia', { params }),
  descargarAdjunto: (emailId, idx) => api.get(`/emails/${emailId}/adjuntos/${idx}`, { responseType: 'blob' }),
  subirAdjuntoADrive: (emailId, idx) => api.post(`/emails/${emailId}/adjuntos/${idx}/subir-drive`),
  analizar: (id, forzar = false) => api.post(`/emails/${id}/analizar`, null, { params: { forzar } }),
  iaPanel: (params = {}) => api.get('/emails/ia-panel', { params }),
  // Lista de un chip de intención del panel IAmail, con el mismo filtro que su contador.
  iaPanelCorreos: (params = {}) => api.get('/emails/ia-panel/correos', { params }),
  // Correos "para administración" sin gestionar de un presupuesto (modal de
  // factura, antes de emitir). Solo lectura.
  paraAdministracion: (presupuestoId) => api.get('/emails/para-administracion', { params: { presupuesto_id: presupuestoId } }),
  driveEstado: () => api.get('/emails/drive/estado'),
  driveDesconectar: () => api.post('/emails/drive/desconectar'),
  // Filtro de spam, publicidad y boletines
  contadoresFiltro: (params = {}) => api.get('/emails/filtro/contadores', { params }),
  moverABandeja: (id) => api.post(`/emails/filtro/${id}/bandeja`),
  marcarSpam: (id) => api.post(`/emails/filtro/${id}/spam`),
  listasFiltro: () => api.get('/emails/filtro/listas'),
  guardarListasFiltro: (data) => api.put('/emails/filtro/listas', data),
  recalcularConocidos: () => api.post('/emails/filtro/recalcular'),
};

// Ayuda IA en IAmail: Claude propone un plan de pasos sobre un correo y el ERP
// los ejecuta por las rutas oficiales solo tras la aprobación del usuario.
export const ayudaIaAPI = {
  estado: () => api.get('/ayuda-ia/estado'),
  listar: (emailId) => api.get('/ayuda-ia/sesiones', { params: { email_id: emailId } }),
  obtener: (id) => api.get(`/ayuda-ia/sesiones/${id}`),
  crear: (emailId, mensaje) => api.post('/ayuda-ia/sesiones', { email_id: emailId, mensaje }),
  mensaje: (id, texto) => api.post(`/ayuda-ia/sesiones/${id}/mensajes`, { mensaje: texto }),
  // body: { plan_id, pasos: [{ id, aprobado, parametros }], confirmar_facturas }
  ejecutar: (id, body) => api.post(`/ayuda-ia/sesiones/${id}/ejecutar`, body),
};

// Vínculo correo -> presupuesto propuesto por la IA nocturna
export const vinculoIaAPI = {
  sugeridos: (presupuestoId) => api.get(`/presupuestos/${presupuestoId}/correos-sugeridos`),
  aceptar: (correoId, presupuestoId = null) => api.post(
    `/correos/${correoId}/vinculo-ia/aceptar`,
    presupuestoId ? { presupuesto_id: presupuestoId } : {}
  ),
  rechazar: (correoId) => api.post(`/correos/${correoId}/vinculo-ia/rechazar`),
  deshacer: (correoId) => api.post(`/correos/${correoId}/vinculo-ia/deshacer`),
};

// Pendientes (libro de tareas del asistente personal: correo + manual)
export const pendientesAPI = {
  listar: (params = {}) => api.get('/pendientes', { params }),
  porHilo: (threadId) => api.get(`/pendientes/por-hilo/${encodeURIComponent(threadId)}`),
  crear: (data) => api.post('/pendientes', data),
  actualizar: (id, data) => api.patch(`/pendientes/${id}`, data),
  eliminar: (id) => api.delete(`/pendientes/${id}`),
  barrido: (params = {}) => api.post('/pendientes/barrido', null, { params }),
  // "No es mío": descarta el pendiente y deja una regla para ese remitente
  // (alcance 'remitente') o para todo su dominio (alcance 'dominio').
  noEsMio: (id, alcance = 'remitente') => api.post(`/pendientes/${id}/no-es-mio`, null, { params: { alcance } }),
  reglas: () => api.get('/pendientes/reglas'),
  eliminarRegla: (id) => api.delete(`/pendientes/reglas/${id}`),
};

export const facturasAPI = {
  descargarPdf: (id) => api.get(`/facturas/${id}/pdf`, { responseType: 'blob' }),
  // Enviar la factura al cliente en un clic (correo propuesto y envío)
  getEnvioCliente: (id) => api.get(`/facturas/${id}/envio-cliente`),
  enviarCliente: (id, body) => api.post(`/facturas/${id}/enviar-cliente`, body),
  listar: (params = {}) => api.get('/finanzas/facturas-emitidas', { params }),
};

// Reclamaciones de cobro (recordatorios por email, historial y observaciones)
export const cobrosReclamacionAPI = {
  vencidas: () => api.get('/cobros-reclamacion/vencidas'),
  plantilla: (facturaId, nivel = 1) => api.get(`/cobros-reclamacion/plantilla/${facturaId}`, { params: { nivel } }),
  enviar: (datos) => api.post('/cobros-reclamacion/enviar', datos),
  observacion: (datos) => api.post('/cobros-reclamacion/observacion', datos),
  historial: (facturaId) => api.get(`/cobros-reclamacion/historial/${facturaId}`),
  contactos: (clienteId) => api.get(`/cobros-reclamacion/contactos/${clienteId}`),
  politica: (clienteId, datos) => api.post(`/cobros-reclamacion/politica/${clienteId}`, datos),
};

// FACe / Facturae (facturación electrónica a administraciones públicas)
export const faceAPI = {
  getEmisor: () => api.get('/face/emisor'),
  updateEmisor: (data) => api.put('/face/emisor', data),
  getDestinos: (clienteId) => api.get(`/face/clientes/${clienteId}/destinos`),
  updateDestinos: (clienteId, data) => api.put(`/face/clientes/${clienteId}/destinos`, data),
  previewFacturae: (facturaId) => api.get(`/face/facturas/${facturaId}/preview`),
  previewFacturaeBorrador: (presupuestoId, data) => api.post(`/face/presupuestos/${presupuestoId}/preview-facturae`, data),
  guardarFirma: (facturaId, xmlFirmadoB64) => api.post(`/face/facturas/${facturaId}/firma`, { xml_firmado_b64: xmlFirmadoB64 }),
  enviarFace: (facturaId) => api.post(`/face/facturas/${facturaId}/enviar`),
  consultarEstadoFace: (facturaId) => api.post(`/face/facturas/${facturaId}/consultar-estado`),
};

// Autocompletado de direcciones (proxy backend de Google Places API New).
export const placesAPI = {
  autocomplete: (q, sessionToken) => api.get('/places/autocomplete', { params: { q, session_token: sessionToken } }),
  detalles: (placeId, sessionToken) => api.get(`/places/detalles/${placeId}`, { params: { session_token: sessionToken } }),
  // modo (propio | extra): quién va al evento, para el coste del personal del transporte
  distancia: (lat, lng, modo) => api.get('/places/distancia', { params: { lat, lng, ...(modo ? { modo } : {}) } }),
  // Sin coordenadas: la ruta con el texto de la dirección
  distanciaDireccion: (direccion, modo) => api.get('/places/distancia', { params: { direccion, ...(modo ? { modo } : {}) } }),
};

// a3doc cloud (gestión documental de la gestoría). Lectura.
export const a3docAPI = {
  estado: () => api.get('/a3doc/estado'),
  arbol: () => api.get('/a3doc/arbol'),
  documentos: (containerId = 1, incluirSubcarpetas = false, limite = 300) =>
    api.get('/a3doc/documentos', { params: { container_id: containerId, incluir_subcarpetas: incluirSubcarpetas, limite } }),
  // encodeURIComponent: los docs "de aplicación" (nóminas) traen ids con '#@#'
  // que romperían la URL (el '#' se interpreta como fragmento) -> "documento no existe".
  descargar: (documentId) => api.get(`/a3doc/descargar/${encodeURIComponent(documentId)}`, { responseType: 'blob' }),
  reintentarCargo: (cargoId) => api.post(`/a3doc/reintentar-cargo/${cargoId}`),
  reintentarCargosLote: (cargoIds) => api.post('/a3doc/reintentar-cargos-lote', { cargo_ids: cargoIds }),
};

// Marketing > Web (leads desde duettocatering.com + sincronizaciones GA4, GSC,
// Google Ads, Mailchimp, uptime, CrUX). Solo lectura para todo salvo leads,
// donde se permite editar estado/notas/vínculos y eliminar.
export const marketingWebAPI = {
  // Dashboard agregado
  kpis: (dias = 30) => api.get('/marketing/web/kpis', { params: { dias } }),

  // Comparativa antes/después del switchover (default 2026-05-15, ventana 30d)
  comparativa: (params = {}) => api.get('/marketing/web/comparativa', { params }),

  // Leads
  listarLeads: (params = {}) => api.get('/marketing/web/leads', { params }),
  obtenerLead: (id) => api.get(`/marketing/web/leads/${id}`),
  actualizarLead: (id, cambios) => api.put(`/marketing/web/leads/${id}`, cambios),
  eliminarLead: (id) => api.delete(`/marketing/web/leads/${id}`),

  // Analytics (GA4) — Fase 2
  analytics: (params = {}) => api.get('/marketing/web/analytics', { params }),
  analyticsSync: (dias = 7) => api.post('/marketing/web/analytics/sync', null, { params: { dias } }),

  // Comportamiento en la web (GA4: páginas, tiempo, scroll, botones, embudo). dias: 7 | 30 | 90
  comportamiento: (dias = 30) => api.get('/marketing/web/comportamiento', { params: { dias } }),
  comportamientoSync: (dias = 7) =>
    api.post('/marketing/web/comportamiento/sync', null, { params: { dias }, timeout: 180000 }),

  // SEO (Search Console) — Fase 3
  seo: (params = {}) => api.get('/marketing/web/seo', { params }),
  seoSync: (dias = 14) => api.post('/marketing/web/seo/sync', null, { params: { dias }, timeout: 180000 }),
  seoPaginaConsultas: (params) => api.get('/marketing/web/seo/pagina-consultas', { params }),
  seoKeywordsObjetivo: () => api.get('/marketing/web/seo/keywords-objetivo'),
  seoKeywordObjetivoCrear: (payload) => api.post('/marketing/web/seo/keywords-objetivo', payload),
  seoKeywordObjetivoEditar: (id, payload) => api.put(`/marketing/web/seo/keywords-objetivo/${id}`, payload),
  seoKeywordObjetivoBorrar: (id) => api.delete(`/marketing/web/seo/keywords-objetivo/${id}`),

  // Email (Mailchimp) — Fase 4
  email: () => api.get('/marketing/web/email'),
  emailSync: () => api.post('/marketing/web/email/sync'),
  emailPlantillas: () => api.get('/marketing/web/email/plantillas'),
  emailCrearCampana: (payload) => api.post('/marketing/web/email/campana', payload, { timeout: 30000 }),
  emailEnviarPrueba: (campaignId, testEmails) =>
    api.post(`/marketing/web/email/campana/${campaignId}/test`, { test_emails: testEmails }, { timeout: 30000 }),

  // Estado de la web — Fase 6 (uptime + CrUX + deploy)
  estado: (dias = 30) => api.get('/marketing/web/estado', { params: { dias } }),
  estadoPing: () => api.post('/marketing/web/estado/ping'),
  estadoCruxSync: () => api.post('/marketing/web/estado/crux-sync'),

  // Google Ads — Fase 5
  ads: (params = {}) => api.get('/marketing/web/ads', { params }),
  adsSync: (dias = 7) => api.post('/marketing/web/ads/sync', null, { params: { dias } }),
  adsConversionesEstado: () => api.get('/marketing/web/ads/conversiones/estado'),
  adsConversionesSubir: (dryRun = false) =>
    api.post('/marketing/web/ads/conversiones/subir', null, { params: { dry_run: dryRun }, timeout: 60000 }),

  // AI Visibility / GEO (Fase 3 del módulo Marketing)
  geoDashboard: () => api.get('/marketing/web/geo/dashboard'),
  geoRun: () => api.post('/marketing/web/geo/run', null, { timeout: 180000 }),
  geoRunCurrent: () => api.get('/marketing/web/geo/runs/current'),
  geoRuns: (limit = 20) => api.get('/marketing/web/geo/runs', { params: { limit } }),
  geoRunDetail: (runId) => api.get(`/marketing/web/geo/runs/${runId}`),
  geoResultDetail: (resultId) => api.get(`/marketing/web/geo/results/${resultId}`),
  geoQueries: () => api.get('/marketing/web/geo/queries'),
  geoCreateQuery: (q) => api.post('/marketing/web/geo/queries', q),
  geoUpdateQuery: (id, q) => api.put(`/marketing/web/geo/queries/${id}`, q),
  geoDeleteQuery: (id) => api.delete(`/marketing/web/geo/queries/${id}`),
  geoTraficoIa: (dias = 90, refresh = false) => api.get('/marketing/web/geo/trafico-ia', { params: { dias, refresh } }),

  // Backlinks — módulo de enlaces SEO comprados (Linkatomic, etc.)
  // F1 lo expone como CRUD + KPIs; verificación HTTP (F2) y sugerencias GSC
  // (F5) llegarán en siguientes commits y los stubs devuelven 503 hasta entonces.
  listarBacklinks: (params = {}) => api.get('/marketing/web/backlinks/', { params }),
  obtenerBacklink: (id) => api.get(`/marketing/web/backlinks/${id}`),
  crearBacklink: (data) => api.post('/marketing/web/backlinks/', data),
  importarBacklinks: (items) =>
    api.post('/marketing/web/backlinks/importar', { items }, { timeout: 120000 }),
  actualizarBacklink: (id, data) => api.put(`/marketing/web/backlinks/${id}`, data),
  eliminarBacklink: (id, hard = false) =>
    api.delete(`/marketing/web/backlinks/${id}`, { params: { hard } }),
  verificarBacklink: (id) => api.post(`/marketing/web/backlinks/${id}/verificar`),
  verificarTodosBacklinks: () =>
    api.post('/marketing/web/backlinks/verificar-todos', null, { timeout: 120000 }),
  historialGscBacklink: (id) => api.get(`/marketing/web/backlinks/${id}/historial-gsc`),
  kpisBacklinks: (mes) =>
    api.get('/marketing/web/backlinks/kpis/resumen', { params: mes ? { mes } : {} }),
  sugerenciasBacklinks: (params = {}) =>
    api.get('/marketing/web/backlinks/sugerencias/proximos', {
      params: { limit: 10, ordenar_por: 'clicks_anuales', min_impresiones: 30, ...params },
      timeout: 60000,  // hasta 60s en cold-cache (Keyword Planner es lento)
    }),
  invalidarCacheSugerencias: () =>
    api.post('/marketing/web/backlinks/sugerencias/invalidar-cache'),
  listarCampanasBacklinks: () =>
    api.get('/marketing/web/backlinks/campanas/listar'),
  obtenerPlanBacklinks: (mes) =>
    api.get('/marketing/web/backlinks/plan/mensual', { params: mes ? { mes } : {} }),
  actualizarPlanBacklinks: (mes, data) =>
    api.put('/marketing/web/backlinks/plan/mensual', data, { params: mes ? { mes } : {} }),
  exportarBacklinksCsvUrl: (mes) =>
    `/marketing/web/backlinks/exportar/csv${mes ? `?mes=${mes}` : ''}`,
};

// ============================================================
// Marketing > Google Business (Places API MVP)
// ============================================================
export const marketingGbpAPI = {
  ficha: (refrescar = false) =>
    api.get('/marketing/google-business/ficha', { params: { refrescar }, timeout: 30000 }),
  sincronizar: () =>
    api.post('/marketing/google-business/sincronizar', null, { timeout: 30000 }),
  listarReviews: (params = {}) =>
    api.get('/marketing/google-business/reviews', { params }),
  sugerirRespuesta: (review_id, place_id) =>
    api.post('/marketing/google-business/sugerir-respuesta-ia', { review_id, place_id }, { timeout: 60000 }),

  // Fase 2 — Business Profile API (cuando Google apruebe)
  estadoFase2: () =>
    api.get('/marketing/google-business/estado-fase2'),
  insightsFase2: (dias = 30) =>
    api.get('/marketing/google-business/insights', { params: { dias } }),
  syncFase2: () =>
    api.post('/marketing/google-business/sync-fase2', null, { timeout: 60000 }),
  responderReview: (review_id, texto) =>
    api.post(`/marketing/google-business/reviews/${review_id}/responder`, { texto }),
  listarPostsGbp: () =>
    api.get('/marketing/google-business/posts'),
  crearPostGbp: (data, publicar = false) =>
    api.post('/marketing/google-business/posts', data, { params: { publicar } }),
  publicarPostGbp: (post_id) =>
    api.post(`/marketing/google-business/posts/${post_id}/publicar`),
  eliminarPostGbp: (post_id) =>
    api.delete(`/marketing/google-business/posts/${post_id}`),
  listarQa: () =>
    api.get('/marketing/google-business/qa'),
  responderQa: (question_id, texto) =>
    api.post(`/marketing/google-business/qa/${question_id}/responder`, { texto }),
};

// ============================================================
// Marketing > Blog
// ============================================================
export const marketingBlogAPI = {
  // Fase 1: inventario + resumen
  resumen: (dias = 30) => api.get('/marketing/blog/resumen', { params: { dias } }),
  inventory: (params = {}) => api.get('/marketing/blog/inventory', { params }),
  inventorySync: (force = false) =>
    api.post('/marketing/blog/inventory/sync', null, { params: { force_scrape: force } }),
  // Fase 2: detalle por post
  post: (slug, dias = 90) => api.get(`/marketing/blog/posts/${slug}`, { params: { dias } }),
  postSyncGsc: (slug, dias = 90) =>
    api.post(`/marketing/blog/posts/${slug}/sync-gsc`, null, { params: { dias } }),
  // Fase 3.5: Plan editorial
  planEditorial: () => api.get('/marketing/blog/plan-editorial'),
  planEditorialCreate: (e) => api.post('/marketing/blog/plan-editorial', e),
  planEditorialUpdate: (id, e) => api.put(`/marketing/blog/plan-editorial/${id}`, e),
  planEditorialDelete: (id) => api.delete(`/marketing/blog/plan-editorial/${id}`),
};


// Remesa de nóminas (hojas de salario de a3doc -> Excel de remesa SEPA del BBVA)
export const nominasAPI = {
  getRemesa: (anio, mes) => api.get(`/nominas/remesa/${anio}/${mes}`),
  generar: (anio, mes) => api.post(`/nominas/remesa/${anio}/${mes}/generar`),
  avisar: (anio, mes) => api.post(`/nominas/remesa/${anio}/${mes}/avisar`),
  marcarPagada: (anio, mes, pagada = true) => api.post(`/nominas/remesa/${anio}/${mes}/pagada`, { pagada }),
  listar: () => api.get('/nominas/remesas'),
  urlExcel: (anio, mes) => `${API_BASE_URL}/nominas/remesa/${anio}/${mes}/excel?token=${encodeURIComponent(getAuthToken() || '')}`,
};


// Obligaciones por empleado (lo que le falta para estar en regla)
export const obligacionesAPI = {
  resumen: () => api.get('/obligaciones-empleados/resumen'),
  empleado: (id) => api.get(`/obligaciones-empleados/${id}`),
  marcar: (id, clave, body) => api.patch(`/obligaciones-empleados/${id}/${clave}`, body),
};

// Acceso público del empleado: crear su contraseña desde el enlace de invitación
export const accesoAPI = {
  validarInvitacion: (token) => api.get(`/acceso/invitacion/${encodeURIComponent(token)}`),
  crearPassword: (token, password) => api.post('/acceso/crear-password', { token, password }),
  // Último acceso con la sesión ya iniciada (el servidor escribe como mucho 1 vez/hora)
  sesionActiva: () => api.post('/acceso/sesion-activa'),
  // Pantalla al entrar de quien llama (ficha leída en el momento) y si ya ha fichado hoy:
  // { pantalla_inicio: 'auto' | 'calendario' | 'mi_inicio', ha_fichado_hoy, tour_pendiente, tour_pospuesto, empleado_id }
  miPantallaInicio: () => api.get('/acceso/mi-pantalla-inicio'),
};

// Gestión documental: plantillas, asignaciones y firma de documentos
export const gestionDocumentalAPI = {
  plantillas: () => api.get('/gestion-documental/plantillas'),
  subirPlantilla: (formData) => api.post('/gestion-documental/plantillas', formData),
  eliminarPlantilla: (id) => api.delete(`/gestion-documental/plantillas/${id}`),
  asignaciones: (params) => api.get('/gestion-documental/asignaciones', { params }),
  estadisticas: () => api.get('/gestion-documental/estadisticas'),
  // Form: plantilla_id, empleados_ids (separados por comas), fecha_limite
  asignar: (plantillaId, empleadosIds, fechaLimite = '') => {
    const fd = new FormData();
    fd.append('plantilla_id', plantillaId);
    fd.append('empleados_ids', (empleadosIds || []).join(','));
    if (fechaLimite) fd.append('fecha_limite', fechaLimite);
    return api.post('/gestion-documental/asignar', fd);
  },
  validar: (id, notas = '') => {
    const fd = new FormData();
    if (notas) fd.append('notas', notas);
    return api.patch(`/gestion-documental/validar/${id}`, fd);
  },
  rechazar: (id, notasRechazo) => {
    const fd = new FormData();
    fd.append('notas_rechazo', notasRechazo);
    return api.patch(`/gestion-documental/rechazar/${id}`, fd);
  },
  misDocumentos: (empleadoId) => api.get('/gestion-documental/mis-documentos', { params: empleadoId ? { empleado_id: empleadoId } : {} }),
  marcarDescargado: (id) => api.patch(`/gestion-documental/marcar-descargado/${id}`),
  subirFirmado: (id, file) => {
    const fd = new FormData();
    fd.append('file', file);
    return api.post(`/gestion-documental/subir-firmado/${id}`, fd);
  },
};

// Portal del empleado (Mi Inicio): una sola lectura con todo lo del empleado
// y las acciones que puede hacer él mismo
export const portalAPI = {
  // params opcionales: { mes, anio } (registro horario) y { empleado_id_param } (dirección)
  miInicio: (params = {}) => api.get('/portal/mi-inicio', { params }),
  // Compatibilidad: el resumen antiguo (solo la parte de Hoy)
  miDashboard: () => api.get('/portal/mi-dashboard'),
  // Cuerpo: { empleado_id, tipo, fecha_inicio, fecha_fin, dias_solicitados, motivo }
  solicitarVacaciones: (body) => api.post('/solicitudes-vacaciones', body),
  cancelarSolicitud: (id) => api.delete(`/solicitudes-vacaciones/${id}`),
  // El propio empleado marca su tour de bienvenida como visto
  tourVisto: () => api.post('/portal/tour-visto'),
  // "Saltar la guía": el tour sigue pendiente pero Mi Inicio deja de abrirlo solo
  tourPosponer: () => api.post('/portal/tour-posponer'),
  // El propio empleado pone o cambia su IBAN (la respuesta trae solo el IBAN enmascarado)
  guardarIban: (iban) => api.put('/portal/mi-iban', { iban }),
};

// Fichaje del propio empleado (entrada, salida y pausas)
export const fichajeAPI = {
  entrada: (empleadoId) => api.post('/fichaje/entrada', { empleado_id: empleadoId }),
  salida: (empleadoId) => api.post('/fichaje/salida', { empleado_id: empleadoId }),
  iniciarPausa: (empleadoId) => api.post('/fichaje/pausa/iniciar', { empleado_id: empleadoId }),
  terminarPausa: (empleadoId) => api.post('/fichaje/pausa/terminar', { empleado_id: empleadoId }),
};

// Documentos de empresa (pack CAE)
export const documentosEmpresaAPI = {
  listar: () => api.get('/documentos-empresa'),
  crear: (body) => api.post('/documentos-empresa', body),
  subir: (formData) => api.post('/documentos-empresa/upload', formData),
  actualizar: (id, body) => api.put(`/documentos-empresa/${id}`, body),
  subirArchivo: (id, formData) => api.post(`/documentos-empresa/${id}/archivo`, formData),
  eliminar: (id) => api.delete(`/documentos-empresa/${id}`),
};

// Uso del ERP (dirección): resumen por persona y envío de eventos del tracker
export const usoErpAPI = {
  // params: { desde, hasta, usuario_id } (fechas AAAA-MM-DD; por defecto 30 días)
  resumen: (params = {}) => api.get('/uso/resumen', { params }),
  enviar: (eventos) => api.post('/uso/eventos', { eventos }),
  estado: () => api.get('/uso/estado'),
  // Lecturas de la noche (revisión con IA): { analisis: [...] }, la más reciente primero
  analisis: (limite = 7) => api.get('/uso/analisis', { params: { limite } }),
  // Sugerencias de cambio: { sugerencias, conteo, conteo_ambito, dias_con_datos, umbral_dias, habilitadas,
  // web_dias_con_datos, web_habilitadas }. Acepta un estado (texto) o { estado, ambito: 'erp' | 'web' }.
  sugerencias: (filtro) => {
    const { estado, ambito } = typeof filtro === 'string' ? { estado: filtro } : (filtro || {});
    const params = {};
    if (estado) params.estado = estado;
    if (ambito) params.ambito = ambito;
    return api.get('/uso/sugerencias', { params });
  },
  // data: { estado: nueva|aceptada|descartada|hecha, nota_direccion }
  actualizarSugerencia: (id, data) => api.patch(`/uso/sugerencias/${id}`, data),
};

// Preferencias de pantalla por usuario (preferencias_usuario_routes.py)
export const preferenciasAPI = {
  // { presupuesto_formato_nuevo: true | false | null (nunca guardado), novedades_vistas: [ids] }
  obtener: () => api.get('/preferencias'),
  // datos: { presupuesto_formato_nuevo?: bool, novedades_vistas?: [ids] }; devuelve el estado guardado.
  // novedades_vistas se SUMA a lo guardado en el servidor (nunca sustituye)
  guardar: (datos) => api.put('/preferencias', datos),
};

// Etiquetas de apertura, elaboración y congelación (etiquetas_routes.py)
export const etiquetasAPI = {
  // { productos, grupos_ingrediente, grupos_plato, puede_editar }
  // tipo (opcional): 'ingrediente' | 'plato'; sin tipo, todos
  productos: (tipo) => api.get('/etiquetas/productos', tipo ? { params: { tipo } } : undefined),
  // body: { conservacion, vida_modo ('dias' | 'envase', solo ingredientes), vida_dias | vida_horas,
  //         vida_congelado_dias, varias_abiertas, se_etiqueta, mantener_fuente }
  // Guardar fija la fuente 'cocina' salvo mantener_fuente: true (dar por buena sin cambiar valores)
  guardarProducto: (tipo, id, body) => api.put(`/etiquetas/productos/${encodeURIComponent(tipo)}/${encodeURIComponent(id)}`, body),
  // items: [{ tipo, producto_id }]
  confirmar: (items) => api.post('/etiquetas/productos/confirmar', { items }),
  // estado: 'abierta' (por defecto) o 'recientes'
  aperturas: (estado = 'abierta') => api.get('/etiquetas/aperturas', { params: { estado } }),
  resumen: () => api.get('/etiquetas/resumen'),
  // body: { tipo, producto_id, accion, caducidad_envase, lote_fabricante, trasvasado, cerrar_ids }
  crear: (body) => api.post('/etiquetas/aperturas', body),
  cambiarEstado: (id, estado) => api.patch(`/etiquetas/aperturas/${encodeURIComponent(id)}`, { estado }),
  congelar: (id) => api.post(`/etiquetas/aperturas/${encodeURIComponent(id)}/congelar`),
  descongelar: (id) => api.post(`/etiquetas/aperturas/${encodeURIComponent(id)}/descongelar`),
  etiquetaHtml: (id) => api.get(`/etiquetas/aperturas/${encodeURIComponent(id)}/etiqueta`, {
    params: { formato: 'html' },
    responseType: 'text',
  }),
};

// PDF de una etiqueta con el token por query (iOS lo abre en una pestaña)
export const etiquetaPdfUrl = (id) => urlConToken(`/api/etiquetas/aperturas/${encodeURIComponent(id)}/etiqueta?formato=pdf`);

// Ajustes > Conexiones: clave de la API de Claude (ajustes_secretos_routes.py).
// El estado nunca trae la clave: { configurada, origen ('entorno' | 'ajustes' | null),
// ultimos4, actualizado_en, actualizado_por_nombre, probado_en, prueba_ok, prueba_detalle }
export const ajustesClavesAPI = {
  estadoAnthropic: () => api.get('/ajustes/claves/anthropic'),
  guardarAnthropic: (clave) => api.put('/ajustes/claves/anthropic', { clave }),
  quitarAnthropic: () => api.delete('/ajustes/claves/anthropic'),
  probarAnthropic: () => api.post('/ajustes/claves/anthropic/probar'),
  // Crédito del mes de la API (estimado por el ERP): {credito, config}
  creditoAnthropic: () => api.get('/ajustes/claves/anthropic/credito'),
  // body: { credito_mensual_usd, dia_renovacion (1-28) }
  guardarCreditoAnthropic: (body) => api.put('/ajustes/claves/anthropic/credito', body),
};
